// --- UPDATED FILE: packages/agent-kit/src/hooks/sprints/useSprintVersions.ts ---

import { useState, useCallback } from 'react';
import useSWR from 'swr';
import { adminSprintService, isApiError } from '@dopomogai/api-client';
import type { SprintVersion, RevertPayload, UUID } from '@dopomogai/types';
import { AgentKitError } from '../../types';

/**
 * A hook for fetching the main "commit" history of an entire sprint.
 * Note: This no longer fetches task history, as tasks are versioned independently.
 * @param sprintId The UUID of the sprint. If undefined, no fetch occurs.
 */
export const useSprintVersionHistory = (sprintId?: UUID) => {
  const swrKey = sprintId ? ['/admin/sprint/versions', sprintId] : null;

  const { data, error, isLoading, mutate } = useSWR<SprintVersion[], AgentKitError>(
    swrKey,
    async ([, sId]: [string, UUID]) => {
      const response = await adminSprintService.listSprintVersions(sId);
      if (isApiError(response)) {
        throw new AgentKitError(`Failed to fetch sprint versions for ${sId}`, response);
      }
      return response;
    }
  );

  return { 
    versions: data, 
    error, 
    isLoading, 
    revalidate: mutate 
  };
};

/**
 * A hook that provides the function for reverting an entire sprint to a past state.
 * This is a "mutation" hook and does not fetch data itself.
 */
export const useRevertSprint = () => {
    const [isReverting, setIsReverting] = useState(false);
    const [error, setError] = useState<AgentKitError | null>(null);

    /**
     * Triggers the revert action for a sprint's structure (days and task assignments).
     * @param sprintId The UUID of the sprint to revert.
     * @param payload The revert payload containing the target version and a commit message.
     * @returns A success/failure object from the API.
     */
    const revertSprint = useCallback(async (sprintId: UUID, payload: RevertPayload): Promise<any | null> => {
        setIsReverting(true);
        setError(null);
        const res = await adminSprintService.revertSprint(sprintId, payload);
        if (isApiError(res)) {
            setError(new AgentKitError('Failed to revert sprint', res));
            setIsReverting(false);
            return null;
        }
        setIsReverting(false);
        return res;
    }, []);
    
    return { 
      revertSprint, 
      isReverting, 
      error 
    };
};